import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Trash2, AlertTriangle, 
  Search, Filter, RefreshCw, User, Target, 
  Trophy, FileText, Image, Eye, ChevronRight,
  CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContentModerationPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('wins');
  const [loading, setLoading] = useState(true);
  const [flaggedContent, setFlaggedContent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null, type: '' });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchFlaggedContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFlaggedContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/moderation/flagged-content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFlaggedContent(data);
      } else {
        toast.error('Failed to load content');
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.item) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      switch (deleteModal.type) {
        case 'win':
          endpoint = `/api/moderation/win/${deleteModal.item.id}`;
          break;
        case 'goal':
          endpoint = `/api/moderation/goal/${deleteModal.item.id}`;
          break;
        case 'checkin':
          endpoint = `/api/moderation/checkin/${deleteModal.item.id}`;
          break;
        case 'profile_picture':
          endpoint = `/api/moderation/user/${deleteModal.item.user_id}/profile-picture`;
          break;
        default:
          toast.error('Unknown content type');
          return;
      }

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Content removed successfully');
        setDeleteModal({ open: false, item: null, type: '' });
        fetchFlaggedContent();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to remove content');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to remove content');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = (item, type) => {
    setDeleteModal({ open: true, item, type });
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'win': return 'Win Post';
      case 'goal': return 'Goal';
      case 'checkin': return 'Check-in';
      case 'profile_picture': return 'Profile Picture';
      default: return 'Content';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const contentTabs = [
    { id: 'wins', label: 'Wins', icon: Trophy, count: flaggedContent?.wins?.length || 0 },
    { id: 'goals', label: 'Goals', icon: Target, count: flaggedContent?.goals?.length || 0 },
    { id: 'checkins', label: 'Check-ins', icon: FileText, count: flaggedContent?.checkins?.length || 0 },
    { id: 'profiles', label: 'Profiles', icon: User, count: flaggedContent?.profiles_with_pictures?.length || 0 },
  ];

  const filterContent = (items) => {
    if (!items) return [];
    if (!searchTerm) return items;
    return items.filter(item => 
      item.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.content?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Content Moderation
                </h1>
                <p className="text-sm text-gray-500">Review and manage user-generated content</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={fetchFlaggedContent}
              className="gap-2"
              data-testid="refresh-button"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by user name, title, or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {contentTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                <Badge variant={activeTab === tab.id ? 'secondary' : 'outline'} className="ml-1">
                  {tab.count}
                </Badge>
              </button>
            );
          })}
        </div>

        {/* Content Lists */}
        <div className="space-y-4">
          {/* Wins Tab */}
          {activeTab === 'wins' && (
            <div className="space-y-4" data-testid="wins-list">
              {filterContent(flaggedContent?.wins).length === 0 ? (
                <EmptyState message="No win posts to review" />
              ) : (
                filterContent(flaggedContent?.wins).map(win => (
                  <ContentCard
                    key={win.id}
                    item={win}
                    type="win"
                    onDelete={() => openDeleteModal(win, 'win')}
                  />
                ))
              )}
            </div>
          )}

          {/* Goals Tab */}
          {activeTab === 'goals' && (
            <div className="space-y-4" data-testid="goals-list">
              {filterContent(flaggedContent?.goals).length === 0 ? (
                <EmptyState message="No goals to review" />
              ) : (
                filterContent(flaggedContent?.goals).map(goal => (
                  <ContentCard
                    key={goal.id}
                    item={goal}
                    type="goal"
                    onDelete={() => openDeleteModal(goal, 'goal')}
                  />
                ))
              )}
            </div>
          )}

          {/* Check-ins Tab */}
          {activeTab === 'checkins' && (
            <div className="space-y-4" data-testid="checkins-list">
              {filterContent(flaggedContent?.checkins).length === 0 ? (
                <EmptyState message="No check-ins to review" />
              ) : (
                filterContent(flaggedContent?.checkins).map(checkin => (
                  <ContentCard
                    key={checkin.id}
                    item={checkin}
                    type="checkin"
                    onDelete={() => openDeleteModal(checkin, 'checkin')}
                  />
                ))
              )}
            </div>
          )}

          {/* Profiles Tab */}
          {activeTab === 'profiles' && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="profiles-list">
              {filterContent(flaggedContent?.profiles_with_pictures).length === 0 ? (
                <div className="col-span-full">
                  <EmptyState message="No profile pictures to review" />
                </div>
              ) : (
                filterContent(flaggedContent?.profiles_with_pictures).map(profile => (
                  <ProfileCard
                    key={profile.id}
                    item={profile}
                    onDelete={() => openDeleteModal(profile, 'profile_picture')}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, item: null, type: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <DialogTitle className="text-center">Delete {getTypeLabel(deleteModal.type)}?</DialogTitle>
            <DialogDescription className="text-center">
              This will permanently remove this content. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteModal.item && (
            <div className="bg-gray-50 rounded-lg p-4 my-4">
              <p className="text-sm text-gray-600">
                <strong>User:</strong> {deleteModal.item.user_name || deleteModal.item.name}
              </p>
              {deleteModal.item.title && (
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Title:</strong> {deleteModal.item.title}
                </p>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setDeleteModal({ open: false, item: null, type: '' })}
              data-testid="cancel-delete"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              className="flex-1"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Content Card Component
const ContentCard = ({ item, type, onDelete }) => {
  const getIcon = () => {
    switch (type) {
      case 'win': return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 'goal': return <Target className="w-5 h-5 text-blue-500" />;
      case 'checkin': return <FileText className="w-5 h-5 text-green-500" />;
      default: return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getContent = () => {
    switch (type) {
      case 'win':
        return item.description;
      case 'goal':
        return item.specific || item.title;
      case 'checkin':
        return item.what_learned || item.challenges;
      default:
        return item.content;
    }
  };

  return (
    <Card className="p-4 hover:shadow-md transition-shadow" data-testid={`content-card-${item.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-gray-900">{item.user_name}</span>
            <span className="text-xs text-gray-400">•</span>
            <span className="text-xs text-gray-500">
              {new Date(item.created_at).toLocaleDateString()}
            </span>
          </div>
          {item.title && (
            <h3 className="font-medium text-gray-800 mb-1">{item.title}</h3>
          )}
          <p className="text-sm text-gray-600 line-clamp-3">{getContent()}</p>
          
          {type === 'goal' && item.deadline && (
            <p className="text-xs text-gray-400 mt-2">Deadline: {item.deadline}</p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
          onClick={onDelete}
          data-testid={`delete-${type}-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};

// Profile Card Component
const ProfileCard = ({ item, onDelete }) => {
  return (
    <Card className="p-4 hover:shadow-md transition-shadow" data-testid={`profile-card-${item.id}`}>
      <div className="flex flex-col items-center text-center">
        <div className="relative mb-3">
          {item.profile_picture ? (
            <img 
              src={item.profile_picture} 
              alt={item.name}
              className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-2xl">
              {item.avatar_emoji || '😊'}
            </div>
          )}
        </div>
        <h3 className="font-semibold text-gray-900">{item.name}</h3>
        <p className="text-sm text-gray-500">{item.email}</p>
        <p className="text-xs text-gray-400 mt-1 capitalize">{item.role}</p>
        
        <Button
          variant="outline"
          size="sm"
          className="mt-3 text-red-600 hover:text-red-700 hover:bg-red-50 gap-2"
          onClick={onDelete}
          data-testid={`reset-picture-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
          Reset Picture
        </Button>
      </div>
    </Card>
  );
};

// Empty State Component
const EmptyState = ({ message }) => (
  <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
    <h3 className="font-semibold text-gray-900">{message}</h3>
    <p className="text-sm text-gray-500 mt-1">All content looks good!</p>
  </div>
);

export default ContentModerationPage;
